import { Localized } from "@fluent/react"
import { CircularProgress, InputAdornment, ListItemText, MenuItem, TextField } from "@mui/material"
import { useAppSelector } from "../../../../app/hooks"
import { useDocumentForms, useFormDefinitions } from "./formSectionLoaders"
import { selectPublishedFormDefinitions, selectUsedFormDefinitions } from "./formSectionSlice"

/**
 * The company's published definitions. One already used in the document is disabled, because a
 * definition may appear only once per document: that is what keeps `{{form.<code>.<key>}}` pointing
 * at a single field.
 */
export const FormDefinitionPicker = ({ documentId, value, onChange }: {
    documentId: string | number
    value: string
    onChange: (definitionId: string) => void
}) => {
    const definitionsState = useFormDefinitions()
    const formsState = useDocumentForms(documentId)
    const definitions = useAppSelector(selectPublishedFormDefinitions)
    const used = useAppSelector(selectUsedFormDefinitions)
    const loading = definitionsState === 'loading' || formsState === 'loading'
    const failed = definitionsState === 'error' || formsState === 'error'

    return <TextField
        select
        fullWidth
        required
        value={value}
        onChange={e => onChange(e.target.value)}
        error={value === '' || failed}
        disabled={formsState !== 'loaded'}
        helperText={failed ? <Localized id="form-section-load-error"><span>_Nie udało się wczytać formularzy</span></Localized> : undefined}
        InputProps={{ endAdornment: loading ? <InputAdornment position="end" sx={{ mr: 3 }}><CircularProgress size={18} /></InputAdornment> : undefined }}
        label={<Localized id="form-section-definition"><span>_Formularz</span></Localized>}
    >
        {definitions.map(definition => {
            const usedBy = used.get(definition.id)
            return <MenuItem key={definition.id} value={definition.id} disabled={usedBy !== undefined}>
                <ListItemText
                    primary={definition.name}
                    secondary={usedBy
                        ? <Localized id="form-section-definition-used" vars={{ section: `${usedBy.numeration}. ${usedBy.name}` }}>
                            <span>{`_Użyty w sekcji ${usedBy.numeration}. ${usedBy.name}`}</span>
                        </Localized>
                        : definition.code}
                />
            </MenuItem>
        })}
    </TextField>
}
